/**
 * Theme entry point. Loaded once, as a module, with defer semantics.
 *
 * Holds only what every page needs: the generic overlay element, the sticky
 * header, the quantity stepper, self-applying selects and the checkout busy
 * state. Everything page-specific is loaded with a
 * dynamic import at the moment of first interaction, so a visitor who never
 * opens the cart never downloads the cart code.
 *
 * Custom elements are used in the light DOM on purpose. Liquid renders the
 * markup, the element only adds behaviour, and Section Rendering API responses
 * can therefore replace that markup wholesale without breaking anything.
 */

import { trapFocus, lockScroll, unlockScroll, prefersReducedMotion, config } from 'utils';

/* ------------------------------------------------------------------ drawer */

/** Visible and in the document: somewhere focus can actually go back to. */
const isFocusable = (element) =>
  element instanceof HTMLElement &&
  element !== document.body &&
  element.isConnected &&
  element.getClientRects().length > 0;

/**
 * Generic overlay. Backs the cart drawer, the mobile menu, the search overlay
 * and the filter drawer, so focus trapping, scroll locking, Escape handling
 * and the closing transition are implemented once.
 *
 * <rr-drawer id="CartDrawer" class="overlay"> ... </rr-drawer>
 * Openers: <button data-drawer-open="CartDrawer">
 * Closers: any [data-drawer-close] inside, plus the scrim.
 * Options:
 *   data-close-above="990"  close (instantly) when the viewport grows past this
 *                           width -- for a drawer that becomes part of the page
 *                           on a desktop, which an iPad rotation can cross.
 *   [data-drawer-panel][data-dialog-on-open]
 *                           the panel is only a dialog while open; inline it is
 *                           ordinary page content and must not claim aria-modal,
 *                           or take focus itself (tabindex), which made a click
 *                           on a filter option focus the panel instead.
 * Other panels carry tabindex="-1" in their markup, so a click on empty space
 * inside them keeps focus -- and Escape and the focus trap -- in the drawer.
 *
 * The look and the motion are the `.overlay` primitive in critical.css; this
 * element only moves the `open` and `closing` attributes that CSS keys off.
 */
class Drawer extends HTMLElement {
  connectedCallback() {
    this.releaseFocus = null;
    this.locked = false;
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    if (this.dataset.closeAbove) {
      this.breakpoint = window.matchMedia(`(min-width: ${this.dataset.closeAbove}px)`);
      this.breakpoint.addEventListener('change', this.onBreakpoint);
    }
  }

  disconnectedCallback() {
    // A Section Rendering swap can remove an open drawer; never leak the lock.
    this.cancelExit();
    this.releaseFocus?.({ restore: false });
    this.releaseFocus = null;
    this.unlock();
    this.breakpoint?.removeEventListener('change', this.onBreakpoint);
  }

  get open() {
    return this.hasAttribute('open');
  }

  get closing() {
    return this.hasAttribute('closing');
  }

  get panel() {
    return this.querySelector('[data-drawer-panel]') || this;
  }

  onClick = (event) => {
    if (!this.open) return;
    if (event.target.closest('[data-drawer-close]') || event.target === this) this.hide();
  };

  /* Only an open drawer owns Escape. Swallowing it unconditionally broke every
     document-level Escape handler for anything that lives inside a drawer
     that happens to be inline at this width, like the filter popovers. */
  onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.open || this.closing) return;
    event.stopPropagation();
    this.hide();
  };

  onBreakpoint = (event) => {
    if (event.matches && this.open) this.hide({ instant: true });
  };

  show(opener) {
    if (this.open && !this.closing) return;
    const panel = this.panel;

    if (this.closing) {
      // Re-opened while it was still leaving: cancel the pending clean-up, or
      // it would land a moment later and shut the drawer that was just opened.
      // The panel transitions back from wherever it had got to.
      this.cancelExit();
      this.removeAttribute('closing');
    } else {
      this.opener = opener || document.activeElement;
      // Reading layout here forces the CLOSED state to be computed before
      // [open] goes on. A drawer that has just been inserted -- or never
      // painted -- otherwise has no start value, and the browser renders it
      // already open: the panel appears without moving and the scrim snaps on.
      panel.getBoundingClientRect();
      this.setAttribute('open', '');
      this.markEntering(panel);
    }
    if (opener) this.opener = opener;

    if (panel.hasAttribute('data-dialog-on-open')) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('tabindex', '-1');
    }

    this.lock();
    this.releaseFocus?.({ restore: false });
    this.releaseFocus = trapFocus(panel, this.querySelector('[data-drawer-initial-focus]'));
    this.setExpanded(true);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  hide({ instant = false } = {}) {
    if (!this.open || this.closing) return;

    // Focus goes back to the page as the exit starts, not when it ends, so a
    // keyboard or screen reader user is never left inside a departing panel.
    const hadFocus = this.contains(document.activeElement) || document.activeElement === document.body;
    this.releaseFocus?.({ restore: false });
    this.releaseFocus = null;
    if (hadFocus) this.returnFocus();
    this.setExpanded(false);

    if (instant || prefersReducedMotion()) {
      this.finish();
      return;
    }

    // Let CSS run the exit transition, then clean up on its completion. The
    // scroll lock is held until then: releasing it at the start brought the
    // scrollbar back mid-slide and shifted the whole page sideways.
    this.setAttribute('closing', '');
    const panel = this.panel;
    this.onExitEnd = (event) => {
      // transitionend bubbles. A hover colour finishing on a button inside the
      // panel used to count as the panel's exit, and the drawer vanished
      // mid-slide. Only the panel's own movement settles it.
      if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
      this.finish();
    };
    panel.addEventListener('transitionend', this.onExitEnd);
    // Guard against a missing or interrupted transition.
    this.exitTimer = setTimeout(this.onExitEnd, 500);
  }

  toggle(opener) {
    this.open && !this.closing ? this.hide() : this.show(opener);
  }

  finish() {
    this.cancelExit();
    this.removeAttribute('open');
    this.removeAttribute('closing');
    this.removeAttribute('data-entering');
    const panel = this.panel;
    if (panel.hasAttribute('data-dialog-on-open')) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('tabindex');
    }
    this.unlock();
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  /**
   * [data-entering] is held for the entrance only, so a component can animate
   * its contents in behind the panel without that animation replaying when
   * the contents are re-rendered while the drawer stays open (the cart).
   */
  markEntering(panel) {
    if (prefersReducedMotion()) return;
    this.setAttribute('data-entering', '');
    const settle = (event) => {
      if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
      panel.removeEventListener('transitionend', settle);
      clearTimeout(timer);
      this.removeAttribute('data-entering');
    };
    const timer = setTimeout(settle, 700);
    panel.addEventListener('transitionend', settle);
  }

  cancelExit() {
    if (this.onExitEnd) this.panel.removeEventListener('transitionend', this.onExitEnd);
    clearTimeout(this.exitTimer);
    this.onExitEnd = null;
  }

  /* Each drawer holds at most one lock and releases only its own, so a
     disconnect racing an exit timer can never release another overlay's. */
  lock() {
    if (this.locked) return;
    lockScroll();
    this.locked = true;
  }

  unlock() {
    if (!this.locked) return;
    unlockScroll();
    this.locked = false;
  }

  /**
   * Back to whatever opened the drawer, if it is still there to take focus.
   * It often is not: after an add, the opener is the add button, which the
   * product form hides once a one-of-one specimen is in the cart. Focus on a
   * hidden element falls to <body>, so fall back to a visible opener for this
   * drawer -- the header cart button, the filter button.
   */
  returnFocus() {
    let target = this.opener;
    if (!isFocusable(target) || this.contains(target)) {
      target = [...document.querySelectorAll(`[data-drawer-open="${this.id}"]`)].find(isFocusable);
    }
    target?.focus({ preventScroll: true });
  }

  setExpanded(state) {
    if (!this.id) return;
    document
      .querySelectorAll(`[data-drawer-open="${this.id}"]`)
      .forEach((opener) => opener.setAttribute('aria-expanded', String(state)));
  }
}

customElements.define('rr-drawer', Drawer);

/* Global opener delegation, so markup rendered later still works. */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!drawer) return;
  event.preventDefault();
  drawer.show(opener);
});

/* ----------------------------------------------------------- sticky header */

/** Scroll distance in one direction before the header reacts to it. */
const HIDE_THRESHOLD = 8;

/**
 * Adds .is-stuck once the header is pinned to the top of the viewport and
 * .is-hidden while scrolling down, so the header gets out of the way on a long
 * specimen page and comes back the moment someone scrolls up.
 *
 * One rAF-throttled passive scroll handler does both. The previous version
 * observed a sentinel it had placed inside the sticky wrapper, which travelled
 * with the header and so never stopped intersecting: the stuck shadow never
 * appeared and --header-offset kept its page-load value for good.
 */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.shopify-section') || this;
    this.sticky = this.dataset.sticky === 'true';
    this.hideOnScroll = this.sticky && this.dataset.hideOnScroll === 'true';
    this.lastY = window.scrollY;
    this.anchorY = this.lastY;
    this.direction = 0;
    this.frame = 0;
    this.published = {};

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  update() {
    const y = Math.max(0, window.scrollY);
    const top = this.wrapper.getBoundingClientRect().top;
    this.classList.toggle('is-stuck', this.sticky && y > 0 && top <= 0.5);

    if (this.hideOnScroll) {
      // A direction only counts once it has been held for a few pixels, so a
      // trackpad's small reversals and iOS's bounce at the ends of the page no
      // longer flick the header in and out.
      const direction = Math.sign(y - this.lastY);
      if (direction !== 0 && direction !== this.direction) {
        this.direction = direction;
        this.anchorY = this.lastY;
      }

      // Never hide while something the header owns is open. Scoped to the
      // desktop panels and drawers: the mobile menu's own submenus are
      // <details> too, and stay open after use, which used to switch
      // hide-on-scroll off for the rest of the visit.
      const blocked = this.querySelector('.mega__details[open]:not([data-closing]), rr-drawer[open]');
      if (blocked || y <= this.offsetHeight * 2) this.classList.remove('is-hidden');
      else if (Math.abs(y - this.anchorY) >= HIDE_THRESHOLD) this.classList.toggle('is-hidden', this.direction > 0);
    }

    this.lastY = y;
    this.publish(top);
  }

  /**
   * Publishes three measurements, because three different questions get asked
   * of them.
   *
   * `--header-height` is the header's own height -- what you subtract to size
   * something against the space beside it.
   *
   * `--header-offset` is how far the header's bottom edge sits from the top of
   * the VIEWPORT right now: the announcement bar's height plus the header's at
   * the top of the page, the header's alone once stuck, nothing while hidden.
   *
   * `--header-shown` is 1 while a sticky header is on screen and 0 while it is
   * hidden or not sticky at all, so something pinned beneath it can write
   * `top: calc(var(--header-height) * var(--header-shown))` and follow it.
   */
  publish(top) {
    const height = this.offsetHeight;
    const hidden = this.classList.contains('is-hidden');
    const shown = this.sticky && !hidden ? 1 : 0;
    const offset = hidden ? 0 : Math.max(0, Math.round(top + height));
    const values = {
      '--header-height': `${height}px`,
      '--header-offset': `${offset}px`,
      '--header-shown': String(shown),
    };

    // Written only on change: a custom property set on <html> restyles the
    // whole document, which is not something to do on every scroll frame.
    const root = document.documentElement.style;
    Object.entries(values).forEach(([name, value]) => {
      if (this.published[name] !== value) root.setProperty(name, value);
    });
    this.published = values;
  }
}

customElements.define('rr-sticky-header', StickyHeader);

/* --------------------------------------------------------------- quantity */

/**
 * Stepper for a number input. Respects Shopify's quantity_rule via the input's
 * own min/max/step, which Liquid renders from the variant.
 */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.addEventListener('click', this.onClick);
    this.input.addEventListener('change', () => this.syncDisabled());
    this.syncDisabled();
  }

  onClick = (event) => {
    const button = event.target.closest('button[data-step]');
    if (!button) return;
    event.preventDefault();
    this.input.stepUp(Number(button.dataset.step) > 0 ? 1 : -1);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  syncDisabled() {
    const value = Number(this.input.value);
    const min = this.input.min === '' ? 1 : Number(this.input.min);
    const max = this.input.max === '' ? Infinity : Number(this.input.max);
    this.querySelector('button[data-step="-1"]')?.toggleAttribute('disabled', value <= min);
    this.querySelector('button[data-step="1"]')?.toggleAttribute('disabled', value >= max);
  }
}

customElements.define('rr-quantity', QuantityInput);

/* ------------------------------------------------------------- auto-submit */

/**
 * A select that only ever changes one thing -- sort order, country, language --
 * applies itself as soon as it changes, rather than waiting on an Apply button
 * beside it. Those buttons are still rendered, for visitors without
 * JavaScript, and hidden from everyone else.
 */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement)) return;
  select.closest('form[data-auto-submit]')?.requestSubmit();
});

/* ---------------------------------------------------------------- checkout */

/**
 * Checkout is a full navigation through /cart to Shopify's checkout, and on a
 * phone that can take a second or two with nothing on screen changing. Mark
 * the button busy the moment it is pressed, so it reads as working rather than
 * as a tap that missed, and swallow a second press so it cannot queue a second
 * post. It is not disabled: a disabled submitter is left out of the form data,
 * and without `checkout` in the post /cart never redirects to payment.
 */
document.addEventListener('submit', (event) => {
  const button = event.submitter;
  if (!button || button.name !== 'checkout') return;
  if (button.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    return;
  }
  button.setAttribute('aria-busy', 'true');
});

/* The back/forward cache restores a page exactly as it was left, busy button
   included. Coming back from checkout must not find it still spinning. */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document
    .querySelectorAll('[name="checkout"][aria-busy="true"]')
    .forEach((button) => button.removeAttribute('aria-busy'));
});

/* ----------------------------------------------------------- image paint */

/**
 * Marks images as painted so CSS can fade them in.
 *
 * Only `loading="lazy"` images are faded — `critical.css` scopes the rule that
 * way, because animating the LCP image delays the LCP event even once the
 * bytes have arrived. A capturing listener is used because `load` does not
 * bubble, and it costs one listener for the whole document rather than one per
 * image, including images that arrive later via the Section Rendering API.
 */
const markLoaded = (image) => image.classList.add('is-loaded');

// The CSS that hides an un-painted lazy image is gated on this class, so the
// hidden state cannot outlive the script that clears it. Set before the sweep
// below, so nothing is hidden without being immediately re-checked.
document.documentElement.classList.add('img-fade');

document.addEventListener(
  'load',
  (event) => {
    if (event.target instanceof HTMLImageElement) markLoaded(event.target);
  },
  true,
);

// Anything already decoded -- from cache, or an eager image that finished
// before this module ran -- never fires `load` again, so sweep once. Eager
// images are included because `.media::before` keeps its shimmer running
// behind any image not yet marked, which is wasted work under an opaque photo.
const sweepLoadedImages = (root = document) => {
  root.querySelectorAll('img').forEach((image) => {
    if (image.complete && image.naturalWidth > 0) markLoaded(image);
  });
};

sweepLoadedImages();

/* ---------------------------------------------------------- lazy features */

/**
 * Feature modules are pulled in on first use rather than up front.
 * Each entry maps a CSS selector to the module that owns it; the module is
 * imported the first time a matching element comes within 200px of the
 * viewport (or the entry's own `rootMargin`), or immediately on the first
 * interaction with one.
 */
const LAZY_FEATURES = [
  { selector: 'rr-nav', module: () => import('nav') },
  { selector: 'rr-product-form', module: () => import('product-form') },
  { selector: 'rr-media-gallery', module: () => import('media-gallery') },
  { selector: 'rr-quick-add', module: () => import('quick-add') },
  /* `rr-product-form` and `rr-quick-add` are in this list on purpose.
     cart-ui.js owns the subscriber that opens the drawer and patches the
     header count after an add, but `rr-cart-drawer` carries `display:
     contents`, so it generates no box, never intersects, and never receives a
     pointer event while the drawer is shut. On a product page nothing
     therefore loaded this module before the add fired: the button showed its
     busy state, the item went in, and nothing else on the page moved. Loading
     it with anything that can add means the subscriber is always listening
     before the add can happen. */
  { selector: 'rr-cart-items, rr-cart-drawer, rr-product-form, rr-quick-add', module: () => import('cart-ui') },
  /* `rr-facets` is `display: contents` (its toolbar has to be able to stick
     against the results beside it), so it never intersects either. Its bar
     does. */
  { selector: 'rr-facets, [data-facets-bar]', module: () => import('facets') },
  { selector: 'rr-recommendations', module: () => import('recommendations') },
  { selector: 'rr-predictive-search', module: () => import('predictive-search') },
  { selector: 'rr-deep-time', module: () => import('deep-time') },
  { selector: 'rr-share', module: () => import('share') },
  /* A screen early, so the random selection has landed before anyone scrolls
     down to it rather than being swapped in while they look at it. */
  { selector: 'rr-shuffle', module: () => import('shuffle'), rootMargin: '100% 0px' },
  /* Half a screen early: the embed is a WebGL page that needs a moment to
     draw, and the still image covers it until it has. */
  { selector: 'rr-globe', module: () => import('globe'), rootMargin: '50% 0px' },
];

const loaded = new Set();

function load(feature) {
  if (loaded.has(feature.selector)) return;
  loaded.add(feature.selector);
  feature.module().catch((error) => {
    loaded.delete(feature.selector);
    console.error(`[theme] failed to load ${feature.selector}`, error);
  });
}

function observe(root = document) {
  LAZY_FEATURES.forEach((feature) => {
    // A loaded module defines its elements, and a defined element upgrades
    // itself wherever it is inserted; there is nothing left to watch for.
    if (loaded.has(feature.selector)) return;
    const elements = root.querySelectorAll(feature.selector);
    if (!elements.length) return;

    // Interaction always wins: a click must not wait for the observer.
    elements.forEach((element) => {
      ['pointerdown', 'focusin', 'keydown'].forEach((type) =>
        element.addEventListener(type, () => load(feature), { once: true, passive: true }),
      );
    });

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          load(feature);
          observer.disconnect();
        }
      },
      { rootMargin: feature.rootMargin || '200px' },
    );
    elements.forEach((element) => observer.observe(element));
  });
}

observe();

/* The theme editor and the Section Rendering API both replace sections at
   runtime; re-scan, and re-sweep for images that were already cached. */
document.addEventListener('shopify:section:load', (event) => {
  observe(event.target);
  if (event.target instanceof Element) sweepLoadedImages(event.target);
});
